import React, { useState } from "react";
import { Bounce, toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { config } from "../../../../../config/config";
import { apiList, invokeApi } from "../../../../../apis/apiServices";
import mobileNoValidation from "../../../../../common/common";
import "./PainterServiceSections.css";

const PainterEnquiry = ({ content }) => {
  const [cookies] = useCookies();
  const { userData } = useSelector((state) => state.user);
  const { locationData } = useSelector((state) => state.location);
  const zoneMappingData = locationData?.countries;

  const [formData, setFormData] = useState({
    email: userData?.user?.email || "",
    customerName: userData?.user?.fullName || "",
    mobileNumber: userData?.user?.mobileNumber || "",
    leadSource: content.leadSource,
    leadType: "Own",
    customerDistrict: userData?.user?.district || "",
    customerState: userData?.user?.state || "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mobileValidate = mobileNoValidation(formData.mobileNumber);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const notify = (type, message) => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      transition: Bounce,
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customerName.trim()) newErrors.customerName = "Full name is required.";
    if (!mobileValidate) newErrors.mobileNumber = "A valid mobile number is required.";
    if (!formData.customerState.trim()) newErrors.customerState = "Please select a state.";
    if (!formData.customerDistrict.trim()) newErrors.customerDistrict = "Please select a city.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const response = await invokeApi(config.apiDomains + apiList.addLead, formData, cookies);
      if (response && (response.status === "200" || response.data.responseMessage === "Successful")) {
        notify("success", "Thank you! Our painting expert will call you soon.");
        setFormData({
          email: "",
          customerName: "",
          mobileNumber: "",
          leadSource: content.leadSource,
          leadType: "Own",
          customerDistrict: "",
          customerState: "",
        });
      } else {
        notify("error", "Sorry, something went wrong. Please try again.");
      }
    } catch (error) {
      notify("error", "An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const trustPoints = [
    { icon: <ScheduleIcon fontSize="inherit" />, text: "Free site visit within 24 hours" },
    { icon: <PriceCheckIcon fontSize="inherit" />, text: "Transparent, no hidden cost quote" },
    { icon: <EngineeringIcon fontSize="inherit" />, text: "Certified, experienced painting crew" },
  ];

  return (
    <section className="pnt-enquiry" id="painter-enquiry">
      <div className="pnt-container pnt-enquiry-grid">
        <div className="pnt-enquiry-left">
          <span className="pnt-tag pnt-tag-light">Free Consultation</span>
          <h2 className="pnt-heading">{content.enquirySection.title}</h2>
          <p className="pnt-subheading pnt-subheading-left pnt-subheading-invert">
            {content.enquirySection.titleDesc}
          </p>
          <div className="pnt-enquiry-points">
            {trustPoints.map((point, index) => (
              <div className="pnt-enquiry-point" key={index}>
                <span className="pnt-enquiry-point-icon">{point.icon}</span>
                <span>{point.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pnt-enquiry-card">
          <form className="pnt-enquiry-form" onSubmit={addLead}>
            <div className="pnt-input-container">
              <input
                placeholder="Full Name *"
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                aria-label="Enter your full name"
              />
              {errors.customerName && <p className="pnt-error">{errors.customerName}</p>}
            </div>
            <div className="pnt-input-container">
              <input
                placeholder="Phone Number *"
                type="tel"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                aria-label="Enter your phone number"
              />
              {errors.mobileNumber && <p className="pnt-error">{errors.mobileNumber}</p>}
            </div>
            <div className="pnt-input-container">
              <input
                placeholder="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                aria-label="Enter your email"
              />
            </div>
            <div className="pnt-enquiry-two-col">
              <div className="pnt-input-container">
                <select
                  value={formData.customerState}
                  name="customerState"
                  onChange={handleChange}
                  aria-label="Select your state"
                >
                  <option value="">Select State</option>
                  {zoneMappingData
                    ?.find((item) => item.countryName === "India")
                    ?.states.map((item, index) => (
                      <option key={index} value={item.stateName}>
                        {item.stateName}
                      </option>
                    ))}
                </select>
                {errors.customerState && <p className="pnt-error">{errors.customerState}</p>}
              </div>
              <div className="pnt-input-container">
                <select
                  value={formData.customerDistrict}
                  name="customerDistrict"
                  onChange={handleChange}
                  disabled={!formData.customerState}
                  aria-label="Select your city"
                >
                  <option value="">Select City</option>
                  {zoneMappingData
                    ?.find((item) => item.countryName === "India")
                    ?.states?.find((state) => state.stateName === formData.customerState)
                    ?.districts?.map((district, index) => (
                      <option key={index} value={district.districtName}>
                        {district.districtName}
                      </option>
                    ))}
                </select>
                {errors.customerDistrict && <p className="pnt-error">{errors.customerDistrict}</p>}
              </div>
            </div>
            <button className="pnt-form-submit pnt-form-submit-full" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Request Free Consultation"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default PainterEnquiry;
